import axios from "axios";

// Token storage. Mirrors curate-ui — when datumcraft.security.enabled
// is the local/standalone default of false, the server ignores the
// header anyway, but the same code path works under JWT-gated
// production deployments.
const TOKEN_KEY = "semantic.auth.token";

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export const api = axios.create({
  baseURL: "/api/v1/semantic",
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ----- Domain types — mirror Java records in semantic-* modules ----

export type IrType =
  | "NUMERIC" | "INTEGER" | "DECIMAL" | "CURRENCY" | "PERCENTAGE"
  | "COUNT" | "STRING" | "BOOLEAN" | "DATE" | "TIMESTAMP"
  | "DURATION" | "UNKNOWN";

export type DialectSidecar = {
  dialect: string;
  verbatimSource: string;
  dialectAttributes: Record<string, string>;
};

export type SemanticIr = {
  id: string;
  irVersion: string;
  name: string;
  description: string | null;
  expression: unknown;
  inferredKind: string;
  inferredType: IrType;
  aggregationLevel: string | null;
  dependencies: string[];
  dialectSidecar: DialectSidecar;
};

export type SemanticDefinition = {
  id: string;
  ir: SemanticIr;
  sourcePlatform: string;
  sourceAsset: string;
  owner: string;
  tenantId: string;
  discoveredAt: string;
};

export type ConflictVariant = {
  definitionId: string;
  platform: string;
  sourceAsset: string;
  formula: string;
  owner: string;
};

export type ConflictReport = {
  clusterId: string;
  displayName: string;
  variantCount: number;
  variants: ConflictVariant[];
  narrative: string;
};

export type FindConflictsResult = {
  tenantId: string;
  definitionCount: number;
  clusterCount: number;
  conflicts: ConflictReport[];
};

export type CanonicalRecommendationAlternative = {
  definitionId: string;
  score: number;
  shortReason: string;
};

export type CanonicalRecommendation = {
  clusterId: string;
  recommendedDefinitionId: string;
  confidence: number;
  rationale: string;
  signalBreakdown: Record<string, number>;
  alternatives: CanonicalRecommendationAlternative[];
};

export type RecommendCanonicalResult = {
  tenantId: string;
  definitionCount: number;
  clusterCount: number;
  recommendations: CanonicalRecommendation[];
};

export type ApprovalRecord = {
  approver: string;
  approvedAt: string;
  action: "DESIGNATE" | "UPDATE" | "DEPRECATE" | "RENEW";
  reason: string;
  previousVersion: number;
};

export type TrustedDefinition = {
  id: string;
  tenantId: string;
  canonicalName: string;
  displayName: string;
  ir: SemanticIr;
  sourceDefinitionId: string | null;
  owner: string;
  status: "ACTIVE" | "DEPRECATED" | "EXPIRED";
  version: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  approvalHistory: ApprovalRecord[];
};

export type DiscoverResult = {
  tenantId: string;
  discoveredCount: number;
  definitionIds: string[];
  extractorsRun: string[];
};

export type DriftSignal = {
  detectedAt: string;
  tenantId: string;
  severity: "LOW" | "MEDIUM" | "HIGH";
  summary: string;
  // Variant-specific fields are present per signal kind. Keep loose.
  [key: string]: unknown;
};

export type DriftResult = {
  tenantId: string;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  signals: DriftSignal[];
  notice: string | null;
};

// ----- API methods -----

export async function discoverStub(tenantId = "_default"): Promise<DiscoverResult> {
  const r = await api.post<DiscoverResult>("/discover", null, { params: { tenantId } });
  return r.data;
}

export async function listConflicts(tenantId = "_default"): Promise<FindConflictsResult> {
  const r = await api.get<FindConflictsResult>("/conflicts", { params: { tenantId } });
  return r.data;
}

export async function listRecommendations(
  tenantId = "_default",
): Promise<RecommendCanonicalResult> {
  const r = await api.get<RecommendCanonicalResult>("/recommendations", { params: { tenantId } });
  return r.data;
}

export async function listTrustedDefinitions(
  tenantId = "_default",
  name?: string,
): Promise<TrustedDefinition[]> {
  const params: Record<string, string> = { tenantId };
  if (name) params.name = name;
  const r = await api.get<TrustedDefinition[]>("/trusted-definitions", { params });
  return r.data;
}

export async function designateCanonical(
  tenantId: string,
  body: { definitionId: string; approver: string; reason: string; owner?: string | null },
): Promise<TrustedDefinition> {
  const r = await api.post<TrustedDefinition>("/trusted-definitions", body, {
    params: { tenantId },
  });
  return r.data;
}

export async function loadDriftReport(tenantId = "_default"): Promise<DriftResult> {
  const r = await api.get<DriftResult>("/drift", { params: { tenantId } });
  return r.data;
}

export async function captureSnapshot(tenantId = "_default"): Promise<unknown> {
  const r = await api.post("/snapshots", null, { params: { tenantId } });
  return r.data;
}

export type RegisteredExtractors = Record<string, string>;

export async function listExtractors(): Promise<RegisteredExtractors> {
  const r = await api.get<RegisteredExtractors>("/discover/extractors");
  return r.data;
}

export async function semanticHealth(): Promise<{ status: string; irVersion: string }> {
  const r = await api.get<{ status: string; irVersion: string; serverTime: string }>("/health");
  return r.data;
}
